/**
 * Unit count HUD — large, always-legible N (GDD: exact to 99, then compact).
 *
 * Contact drain: when the horde reaches the squad, sim aggregate combat lowers
 * `snap.n` and this HUD flashes hit feedback so the count drain is readable.
 */

import { formatCountDisplay } from "@afr/sim";
import type { SimSnapshot } from "@afr/sim";

export interface UnitCountHud {
  readonly root: HTMLElement;
  update(snap: SimSnapshot): void;
  dispose(): void;
}

export interface UnitCountHudOptions {
  readonly parent?: HTMLElement;
}

/** Hit flash duration after squad loses units to horde contact (ms). */
const HIT_FLASH_MS = 220;

/**
 * Create a DOM overlay for unit count, phase, wave HP, and DPS.
 */
export function createUnitCountHud(
  options: UnitCountHudOptions = {},
): UnitCountHud {
  const root = document.createElement("div");
  root.className = "afr-hud";
  root.setAttribute("data-testid", "unit-count-hud");
  Object.assign(root.style, {
    position: "absolute",
    inset: "0",
    pointerEvents: "none",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    color: "#f8fafc",
    zIndex: "10",
  } satisfies Partial<CSSStyleDeclaration>);

  const countEl = document.createElement("div");
  countEl.setAttribute("data-testid", "unit-count");
  Object.assign(countEl.style, {
    position: "absolute",
    top: "12%",
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: "clamp(48px, 14vw, 96px)",
    fontWeight: "800",
    letterSpacing: "-0.03em",
    textShadow: "0 2px 12px rgba(0,0,0,0.65), 0 0 40px rgba(96,165,250,0.35)",
    lineHeight: "1",
    transition: "color 80ms linear, transform 80ms ease-out",
  } satisfies Partial<CSSStyleDeclaration>);
  countEl.textContent = "0";
  root.appendChild(countEl);

  // Red vignette pulse on horde contact hits (sim N drain)
  const hitFx = document.createElement("div");
  hitFx.setAttribute("data-testid", "horde-hit-feedback");
  Object.assign(hitFx.style, {
    position: "absolute",
    inset: "0",
    background:
      "radial-gradient(ellipse at 50% 40%, rgba(248,113,113,0.0) 30%, rgba(127,29,29,0.55) 100%)",
    opacity: "0",
    transition: "opacity 90ms linear",
    pointerEvents: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  root.appendChild(hitFx);

  const metaEl = document.createElement("div");
  metaEl.setAttribute("data-testid", "hud-meta");
  Object.assign(metaEl.style, {
    position: "absolute",
    top: "calc(12% + clamp(52px, 15vw, 100px))",
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: "clamp(12px, 3.2vw, 16px)",
    fontWeight: "600",
    opacity: "0.9",
    textAlign: "center",
    textShadow: "0 1px 6px rgba(0,0,0,0.7)",
    whiteSpace: "nowrap",
  } satisfies Partial<CSSStyleDeclaration>);
  root.appendChild(metaEl);

  const waveEl = document.createElement("div");
  waveEl.setAttribute("data-testid", "wave-hp");
  Object.assign(waveEl.style, {
    position: "absolute",
    top: "4%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(70%, 280px)",
    height: "8px",
    borderRadius: "4px",
    background: "rgba(15,23,42,0.55)",
    overflow: "hidden",
    boxShadow: "0 0 0 1px rgba(248,250,252,0.15)",
    display: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  const waveFill = document.createElement("div");
  Object.assign(waveFill.style, {
    height: "100%",
    width: "100%",
    background: "linear-gradient(90deg, #f87171, #ef4444)",
    transformOrigin: "left center",
    transform: "scaleX(1)",
    transition: "transform 80ms linear",
  } satisfies Partial<CSSStyleDeclaration>);
  waveEl.appendChild(waveFill);
  root.appendChild(waveEl);

  const hintEl = document.createElement("div");
  Object.assign(hintEl.style, {
    position: "absolute",
    bottom: "6%",
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: "12px",
    opacity: "0.55",
    textAlign: "center",
  } satisfies Partial<CSSStyleDeclaration>);
  hintEl.textContent = "A/D or drag · gates · pedestal → mecha · climax";
  root.appendChild(hintEl);

  const parent = options.parent ?? document.body;
  parent.appendChild(root);

  let lastN: number | null = null;
  let lastMechaHp: number | null = null;
  let hitUntil = 0;
  let hitTimer: ReturnType<typeof setTimeout> | null = null;

  const pulseHit = (): void => {
    hitUntil = performance.now() + HIT_FLASH_MS;
    countEl.style.color = "#fca5a5";
    countEl.style.transform = "translateX(-50%) scale(1.08)";
    countEl.style.textShadow =
      "0 2px 12px rgba(0,0,0,0.65), 0 0 36px rgba(248,113,113,0.65)";
    hitFx.style.opacity = "1";
    if (hitTimer) clearTimeout(hitTimer);
    hitTimer = setTimeout(() => {
      countEl.style.transform = "translateX(-50%) scale(1)";
      countEl.style.textShadow =
        "0 2px 12px rgba(0,0,0,0.65), 0 0 40px rgba(96,165,250,0.35)";
      hitFx.style.opacity = "0";
      hitTimer = null;
    }, HIT_FLASH_MS);
  };

  const update = (snap: SimSnapshot): void => {
    // Hit feedback when horde contact drains squad count (or mecha HP) via sim
    if (!snap.inMecha) {
      if (lastN !== null && snap.n < lastN) {
        pulseHit();
      }
      lastN = snap.n;
      lastMechaHp = null;
    } else if (snap.mecha) {
      if (lastMechaHp !== null && snap.mecha.hp < lastMechaHp - 0.5) {
        pulseHit();
      }
      lastMechaHp = snap.mecha.hp;
      lastN = null;
    }

    const flashing = performance.now() < hitUntil;

    if (snap.inMecha && snap.mecha) {
      countEl.textContent = `M ${Math.ceil(snap.mecha.hp)}`;
      if (!flashing) countEl.style.color = "#cbd5e1";
    } else {
      countEl.textContent = formatCountDisplay(snap.n);
      if (!flashing) countEl.style.color = "#f8fafc";
    }

    const bits: string[] = [snap.phase];
    if (snap.friendlyDps > 0) bits.push(`DPS ${Math.round(snap.friendlyDps)}`);
    if (snap.powerTokens > 0) bits.push(`+${snap.powerTokens} tok`);
    if (snap.activeWaveId) bits.push("horde");
    metaEl.textContent = bits.join(" · ");

    if (
      snap.activeWaveId &&
      snap.activeWaveMaxHp > 0 &&
      snap.activeWaveHp > 0
    ) {
      waveEl.style.display = "block";
      const frac = Math.max(
        0,
        Math.min(1, snap.activeWaveHp / snap.activeWaveMaxHp),
      );
      waveFill.style.transform = `scaleX(${frac})`;
    } else {
      waveEl.style.display = "none";
    }

    if (snap.phase === "results_win") {
      metaEl.textContent = "WIN";
      metaEl.style.color = "#34d399";
    } else if (snap.phase === "results_lose" || snap.phase === "continue_offer") {
      metaEl.textContent =
        snap.phase === "continue_offer" ? "CONTINUE?" : "LOSE";
      metaEl.style.color = "#f87171";
    } else if (snap.phase === "pedestal") {
      metaEl.textContent = "TRANSFORM";
      metaEl.style.color = "#93c5fd";
    } else if (snap.phase === "climax") {
      metaEl.textContent = bits.join(" · ");
      metaEl.style.color = "#fbbf24";
    } else {
      metaEl.style.color = "#f8fafc";
    }
  };

  const dispose = (): void => {
    if (hitTimer) clearTimeout(hitTimer);
    root.remove();
  };

  return { root, update, dispose };
}
